import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { arrayMove } from "@dnd-kit/sortable";
import type { DragEndEvent } from "@dnd-kit/core";

import { Button } from "@/components/ui/button";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";
import { noticesApi } from "@/lib/api/notices";

// FSD imports
import {
  useNoticeCategories,
  useCreateNoticeCategory,
  useUpdateNoticeCategory,
  useDeleteNoticeCategory,
} from "@/features/notice";
import { DEFAULT_NOTICE_COLOR, type NoticeCategory } from "@/entities/notice";
import { CategoryList, CategoryDialog } from "@/widgets/notice";

function NoticeCategoriesPage() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [selectedColor, setSelectedColor] =
    useState<string>(DEFAULT_NOTICE_COLOR);
  const [editingCategory, setEditingCategory] = useState<NoticeCategory | null>(
    null
  );

  const { data: categories, refetch } = useNoticeCategories();
  const createCategory = useCreateNoticeCategory();
  const updateCategory = useUpdateNoticeCategory();
  const deleteCategory = useDeleteNoticeCategory();
  const { setAlertDialogState, resetAlertDialogState } = useAlertDialogStore();

  const [items, setItems] = useState<NoticeCategory[]>(categories || []);

  useEffect(() => {
    if (categories) {
      setItems(categories);
    }
  }, [categories]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = items.findIndex((item) => item._id === active.id);
      const newIndex = items.findIndex((item) => item._id === over?.id);

      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);

      try {
        await noticesApi.reorderCategories(newItems.map((item) => item._id));
        refetch();
      } catch {
        toast.error("순서 변경에 실패했습니다.");
        setItems(items);
      }
    }
  };

  const handleCreate = async () => {
    if (!categoryName.trim()) {
      toast.error("카테고리 이름을 입력해주세요.");
      return;
    }

    try {
      await createCategory.mutateAsync({
        name: categoryName,
        order: categories?.length || 0,
        color: selectedColor,
      });
      setCategoryName("");
      setSelectedColor(DEFAULT_NOTICE_COLOR);
      setIsCreateDialogOpen(false);
      toast.success("카테고리가 생성되었습니다.");
    } catch (error) {
      console.error("Error:", error);
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.message || "카테고리 생성에 실패했습니다.";
        toast.error(errorMessage);
      } else {
        toast.error("카테고리 생성에 실패했습니다.");
      }
    }
  };

  const handleEdit = async () => {
    if (!editingCategory || !categoryName.trim()) {
      toast.error("카테고리 이름을 입력해주세요.");
      return;
    }

    try {
      await updateCategory.mutateAsync({
        id: editingCategory._id,
        data: { name: categoryName, color: selectedColor },
      });
      setCategoryName("");
      setSelectedColor(DEFAULT_NOTICE_COLOR);
      setEditingCategory(null);
      setIsEditDialogOpen(false);
      toast.success("카테고리가 수정되었습니다.");
    } catch (error) {
      console.error("Error:", error);
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.message || "카테고리 수정에 실패했습니다.";
        toast.error(errorMessage);
      } else {
        toast.error("카테고리 수정에 실패했습니다.");
      }
    }
  };

  const handleDelete = (id: string) => {
    setAlertDialogState({
      title: "카테고리 삭제",
      description:
        "정말 삭제하시겠습니까? 이 카테고리의 공지사항은 '일반' 카테고리로 이동됩니다.",
      confirmText: "삭제",
      onConfirm: async () => {
        resetAlertDialogState();
        try {
          await deleteCategory.mutateAsync(id);
          toast.success("카테고리가 삭제되었습니다.");
        } catch (error) {
          console.error("Error:", error);
          if (error instanceof AxiosError) {
            const errorMessage =
              error.response?.data?.message || "카테고리 삭제에 실패했습니다.";
            toast.error(errorMessage);
          } else {
            toast.error("카테고리 삭제에 실패했습니다.");
          }
        }
      },
      onCancel: () => {
        resetAlertDialogState();
      },
    });
  };

  const openEditDialog = (category: NoticeCategory) => {
    setEditingCategory(category);
    setCategoryName(category.name);
    setSelectedColor(category.color || DEFAULT_NOTICE_COLOR);
    setIsEditDialogOpen(true);
  };

  return (
    <PageContainer>
      <Header
        title="카테고리 관리"
        right={
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="size-4 mr-2" />
            카테고리 추가
          </Button>
        }
      />

      <div className="w-full">
        <div className="space-y-3">
          <CategoryList
            items={items}
            onDragEnd={handleDragEnd}
            onEdit={openEditDialog}
            onDelete={handleDelete}
          />
        </div>
      </div>

      {/* 생성 다이얼로그 */}
      <CategoryDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        mode="create"
        categoryName={categoryName}
        onCategoryNameChange={setCategoryName}
        selectedColor={selectedColor}
        onColorChange={setSelectedColor}
        onSubmit={handleCreate}
        isPending={createCategory.isPending}
      />

      {/* 수정 다이얼로그 */}
      <CategoryDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        mode="edit"
        categoryName={categoryName}
        onCategoryNameChange={setCategoryName}
        selectedColor={selectedColor}
        onColorChange={setSelectedColor}
        onSubmit={handleEdit}
        isPending={updateCategory.isPending}
        isNameDisabled={editingCategory?.name === "일반"}
      />
    </PageContainer>
  );
}

export const Route = createFileRoute("/_content/notice/categories")({
  component: NoticeCategoriesPage,
});
